/**
 * Flow `.flow` bundle format.
 *
 * A `.flow` file is now a ZIP archive (instead of a plain JSON) with the
 * following internal layout:
 *
 *   mychart.flow                 (zip)
 *   ├── beatmap.meta.bin         the chart JSON (FlowChartFile, UTF-8)
 *   ├── cover.<png|jpg|webp>     optional album cover art
 *   └── <song>.<mp3|ogg|wav|…>   the audio file (any common format)
 *
 * `track.data.json` is intentionally NOT written — it had no consumer.
 *
 * The bundle keeps the chart, cover, and audio glued together so the user
 * can ship one file around (email, drag-drop, version-control as a binary)
 * and never lose the audio that pairs with it.
 *
 * Audio + cover are stored with no extra compression (`STORE`) because they
 * are already compressed binary; recompressing them in zip just burns CPU
 * for ~0% size savings. The JSON manifest is deflated.
 */

import JSZip from 'jszip';

/**
 * `Object.values(zip.files)` is typed as `unknown[]` when TS can't resolve
 * jszip's own types (e.g. right after a fresh install before the dev
 * server restart). This minimal local type covers the fields we actually
 * touch — name, dir, async — so the file compiles even when full type
 * inference is unavailable, and degrades gracefully to the real JSZip
 * types when they do resolve.
 */
type ZipFile = {
  name: string;
  dir: boolean;
  async(type: 'string'): Promise<string>;
  async(type: 'arraybuffer'): Promise<ArrayBuffer>;
};

/** Name of the chart-data file inside the bundle. */
export const BUNDLE_CHART_NAME = 'beatmap.meta.bin';

/** Filename prefixes we accept when scanning a bundle for the cover. */
export const COVER_PREFIX = 'cover';

/** Audio MIME → extension lookup. Used when packing an audio file with a
 *  known MIME but no filename. */
export const AUDIO_EXT_FROM_MIME: Record<string, string> = {
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/wave': 'wav',
  'audio/flac': 'flac',
  'audio/x-flac': 'flac',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
};

/** Reverse map for sniffing MIME from extension on the way out. */
export const AUDIO_MIME_FROM_EXT: Record<string, string> = {
  mp3: 'audio/mpeg',
  ogg: 'audio/ogg',
  wav: 'audio/wav',
  flac: 'audio/flac',
  m4a: 'audio/mp4',
};

const COVER_EXTS = ['png', 'jpg', 'jpeg', 'webp', 'gif'] as const;
const AUDIO_EXTS = ['mp3', 'ogg', 'wav', 'flac', 'm4a'] as const;

export interface BundleInputs {
  /** Raw JSON string of the chart (already serialized FlowChartFile). */
  chartJson: string;
  /** Optional cover image bytes + filename (with extension). */
  cover?: { bytes: ArrayBuffer; filename: string } | null;
  /** Optional audio bytes + filename (with extension). */
  audio?: { bytes: ArrayBuffer; filename: string } | null;
  /** Optional visualizer files to embed at the ZIP root (name + raw JSON) so the
   *  visual travels inside the .flow and can be opened in the game. */
  visualizers?: { name: string; json: string }[];
}

export interface BundleContents {
  chartJson: string;
  cover: { bytes: ArrayBuffer; filename: string; mime: string } | null;
  audio: { bytes: ArrayBuffer; filename: string; mime: string } | null;
}

/** Pack a chart + cover + audio into a single zip Blob. */
export async function packBundle(inputs: BundleInputs): Promise<Blob> {
  const zip = new JSZip();
  zip.file(BUNDLE_CHART_NAME, inputs.chartJson, {
    binary: false,
    compression: 'DEFLATE',
  });
  if (inputs.cover) {
    const ext = extensionFromFilename(inputs.cover.filename) || 'png';
    zip.file(`${COVER_PREFIX}.${ext}`, inputs.cover.bytes, {
      binary: true,
      compression: 'STORE',
    });
  }
  if (inputs.audio) {
    const safeName = sanitizeFilename(inputs.audio.filename);
    zip.file(safeName, inputs.audio.bytes, {
      binary: true,
      compression: 'STORE',
    });
  }
  for (const v of inputs.visualizers ?? []) {
    if (!v?.name || v.json == null) continue;
    zip.file(sanitizeFilename(v.name), v.json, { binary: false, compression: 'DEFLATE' });
  }
  return zip.generateAsync({ type: 'blob', mimeType: 'application/zip' });
}

/**
 * Unpack a `.flow` bundle. Handles both the new zip format and (as a
 * compatibility fallback) the legacy plain-JSON `.flow` files so older
 * charts on disk still load. We sniff the first byte: zip archives start
 * with `PK\x03\x04` (0x50 0x4B 0x03 0x04).
 */
export async function unpackBundle(source: Blob | ArrayBuffer): Promise<BundleContents> {
  const buffer = source instanceof Blob ? await source.arrayBuffer() : source;
  if (looksLikeZip(buffer)) {
    return readZip(buffer);
  }
  // Legacy fallback — treat the whole payload as the chart JSON.
  const text = new TextDecoder('utf-8').decode(new Uint8Array(buffer));
  return { chartJson: text, cover: null, audio: null };
}

async function readZip(buffer: ArrayBuffer): Promise<BundleContents> {
  const zip = await JSZip.loadAsync(buffer);

  // Chart JSON — the canonical name, with a fallback to any *.json file
  // at the root so we tolerate renamed bundles.
  const allFiles = Object.values(zip.files) as ZipFile[];
  let chartEntry: ZipFile | null = (zip.file(BUNDLE_CHART_NAME) as ZipFile | null) ?? null;
  if (!chartEntry) {
    const rootJson = allFiles.find(
      f => !f.dir && /\.json$/i.test(f.name) && !f.name.includes('/'),
    );
    chartEntry = rootJson || null;
  }
  if (!chartEntry) {
    throw new Error('Bundle is missing the chart file (beatmap.meta.bin).');
  }
  const chartJson = await chartEntry.async('string');

  // Cover — any file at the root whose name starts with "cover." and has
  // a recognized image extension.
  let cover: BundleContents['cover'] = null;
  for (const f of allFiles) {
    if (f.dir) continue;
    if (f.name.includes('/')) continue;
    const lower = f.name.toLowerCase();
    if (!lower.startsWith(`${COVER_PREFIX}.`)) continue;
    const ext = extensionFromFilename(lower);
    if (!ext || !COVER_EXTS.includes(ext as any)) continue;
    const bytes = await f.async('arraybuffer');
    cover = {
      bytes,
      filename: f.name,
      mime: imageMimeFromExt(ext) ?? 'image/png',
    };
    break;
  }

  // Audio — first root file with a recognized audio extension (other
  // than the chart JSON).
  let audio: BundleContents['audio'] = null;
  for (const f of allFiles) {
    if (f.dir) continue;
    if (f.name.includes('/')) continue;
    if (f.name === BUNDLE_CHART_NAME) continue;
    const ext = extensionFromFilename(f.name);
    if (!ext || !AUDIO_EXTS.includes(ext as any)) continue;
    const bytes = await f.async('arraybuffer');
    audio = {
      bytes,
      filename: f.name,
      mime: AUDIO_MIME_FROM_EXT[ext] || 'application/octet-stream',
    };
    break;
  }

  return { chartJson, cover, audio };
}

// ── Helpers ────────────────────────────────────────────────────────────────

function looksLikeZip(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 4) return false;
  const view = new Uint8Array(buffer, 0, 4);
  return view[0] === 0x50 && view[1] === 0x4b && view[2] === 0x03 && view[3] === 0x04;
}

export function extensionFromFilename(name: string): string | null {
  const m = /\.([^.]+)$/.exec(name);
  return m ? m[1].toLowerCase() : null;
}

function imageMimeFromExt(ext: string): string | null {
  switch (ext) {
    case 'png': return 'image/png';
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'webp': return 'image/webp';
    case 'gif': return 'image/gif';
    default: return null;
  }
}

function sanitizeFilename(name: string): string {
  // Strip path components and reserved chars, keep the extension.
  const base = name.split(/[\\/]/).pop() || 'audio';
  return base.replace(/[^\w\-. ]+/g, '_').replace(/\s+/g, '_');
}
